import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

// Glowing volumes marking the available residences, replacing the pulsing dot
// markers. Each block is the apartment's actual massing from the model, so the
// highlight sits exactly on the thing it is selling rather than floating over
// it, and picking one opens that residence.
//
// The shader is a fresnel shell rather than a solid fill: a solid box would
// hide the facade underneath, which is the part worth looking at. Edges glow,
// the middle stays largely transparent, and a scan line rises through the
// volume so the marker reads as live without the jitter of a pulsing sprite.

// The export names the three volumes inconsistently (an underscore on two, a
// hyphen and different casing on the third), so matching is done on a
// normalised key rather than the literal strings.
const BLOCK_TYPES: { match: string; apartmentIndex: number; label: string; caption: string }[] = [
    { match: '2bedroom', apartmentIndex: 1, label: '2 Bedroom', caption: 'Courtyard aspect' },
    { match: '3bedroom', apartmentIndex: 2, label: '3 Bedroom', caption: 'Wadi aspect' },
    { match: 'penthouse', apartmentIndex: 3, label: 'Penthouse', caption: 'Upper terrace' },
];

const normalise = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

const VERTEX = /* glsl */ `
  uniform float uMinY;
  uniform float uMaxY;
  varying vec3 vNormalW;
  varying vec3 vViewW;
  varying float vHeight;
  void main() {
    vec4 world = modelMatrix * vec4(position, 1.0);
    vNormalW = normalize(mat3(modelMatrix) * normal);
    vViewW = normalize(cameraPosition - world.xyz);
    // 0 at the base of the block, 1 at the top, for the rising scan. Taken from
    // object-space height rather than uv.y: these volumes are massing exported
    // from the masterplan and are not guaranteed to carry a UV set at all.
    vHeight = clamp((position.y - uMinY) / max(uMaxY - uMinY, 0.0001), 0.0, 1.0);
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const FRAGMENT = /* glsl */ `
  uniform vec3 uColor;
  uniform float uTime;
  uniform float uHover;
  uniform float uOpacity;
  varying vec3 vNormalW;
  varying vec3 vViewW;
  varying float vHeight;

  void main() {
    // Edge emphasis, not a pure fresnel shell.
    //
    // A fresnel alone is the obvious choice and it is wrong for these: they are
    // boxes, and a box presents almost no grazing area to a typical viewpoint,
    // so the rim term collapses to zero across every face you can actually see
    // and the marker renders as nothing. Measured: identical pixels with the
    // blocks on and off. So the glow is carried by a real volumetric fill, with
    // the fresnel only sharpening the silhouette on top of it.
    float facing = abs(dot(normalize(vNormalW), normalize(vViewW)));
    float rim = pow(1.0 - facing, 1.6);

    // Slow breathing, plus a scan sweeping upward through the volume.
    float breathe = 0.86 + 0.14 * sin(uTime * 1.7);
    float scan = smoothstep(0.26, 0.0, abs(fract(vHeight - uTime * 0.16) - 0.5) - 0.3);

    // Fades toward the top so the volume reads as rising out of the massing
    // rather than as a sealed crate sitting on it.
    float lift = mix(1.0, 0.45, smoothstep(0.35, 1.0, vHeight));

    // Alpha carries the shape, colour stays saturated. Additive blending was
    // tried first and washes out: over a sunlit facade it just drives every
    // channel to white, so the marker reads as a blown highlight rather than as
    // a coloured volume. A tinted overlay holds its hue on any backdrop.
    float alpha = (0.2 + rim * 0.55 + scan * 0.35) * breathe * lift;
    alpha *= mix(1.0, 1.75, uHover);
    vec3 tint = uColor * (1.0 + rim * 0.9 + scan * 0.5);
    gl_FragColor = vec4(tint, clamp(alpha, 0.0, 0.92) * uOpacity);
  }
`;

export interface ApartmentBlock {
    mesh: THREE.Mesh;
    apartmentIndex: number;
    label: string;
    caption: string;
    element: HTMLElement;
    hover: number;
}

export interface ApartmentBlocksRig {
    readonly ready: boolean;
    setVisible(visible: boolean): void;
    /** Show only one residence type; null shows all. */
    setFilter(apartmentIndex: number | null): void;
    update(dt: number, camera: THREE.PerspectiveCamera): void;
    dispose(): void;
}

export function createApartmentBlocks(opts: {
    url: string;
    parent: THREE.Object3D;
    domElement: HTMLElement;
    labelHost: HTMLElement;
    camera: THREE.PerspectiveCamera;
    onSelect: (apartmentIndex: number) => void;
}): ApartmentBlocksRig {
    const { url, parent, domElement, labelHost, camera, onSelect } = opts;

    const group = new THREE.Group();
    group.name = 'apartment-blocks';
    parent.add(group);

    const blocks: ApartmentBlock[] = [];
    const uniformsOf = new Map<THREE.Mesh, { uTime: THREE.IUniform<number>; uHover: THREE.IUniform<number> }>();
    let ready = false;
    let visible = true;
    let filter: number | null = null;
    let time = 0;

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let hovered: ApartmentBlock | null = null;

    // Chosen against warm sandstone at golden hour, which is what these sit on.
    // A gold penthouse marker was invisible for exactly that reason: the marker
    // has to contrast with the building, not match it.
    const COLORS: Record<number, THREE.Color> = {
        1: new THREE.Color('#22d3ff'),
        2: new THREE.Color('#8b7bff'),
        3: new THREE.Color('#ff5fb0'),
    };

    const loader = new GLTFLoader();
    const draco = new DRACOLoader();
    draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    loader.setDRACOLoader(draco);

    loader.load(
        url,
        (gltf) => {
            gltf.scene.traverse((child) => {
                const mesh = child as THREE.Mesh;
                if (!mesh.isMesh) return;
                const key = normalise(mesh.name || mesh.parent?.name || '');
                const type = BLOCK_TYPES.find((t) => key.includes(t.match));
                if (!type) return;

                mesh.geometry.computeBoundingBox();
                const bb = mesh.geometry.boundingBox!;
                const uniforms = {
                    uColor: { value: COLORS[type.apartmentIndex] },
                    uTime: { value: 0 },
                    uHover: { value: 0 },
                    uOpacity: { value: 0.95 },
                    uMinY: { value: bb.min.y },
                    uMaxY: { value: bb.max.y },
                };
                mesh.material = new THREE.ShaderMaterial({
                    vertexShader: VERTEX,
                    fragmentShader: FRAGMENT,
                    uniforms,
                    transparent: true,
                    depthWrite: false,
                    // Drawn over the massing rather than depth-tested against
                    // it. These volumes ARE the buildings' envelopes, so with a
                    // depth test they lose to the facade they enclose almost
                    // everywhere and the marker simply never appears. As an
                    // overlay the glow also stays readable when a residence is
                    // behind another block, which is what a marker is for.
                    depthTest: false,
                    side: THREE.DoubleSide,
                    fog: false,
                });
                mesh.renderOrder = 5;
                uniformsOf.set(mesh, uniforms);

                const element = buildLabel(type.label, type.caption, COLORS[type.apartmentIndex]);
                labelHost.appendChild(element);
                blocks.push({
                    mesh,
                    apartmentIndex: type.apartmentIndex,
                    label: type.label,
                    caption: type.caption,
                    element,
                    hover: 0,
                });
            });
            group.add(gltf.scene);
            ready = true;
            console.info(`[blocks] ${blocks.length} residence volumes ready`);
        },
        undefined,
        (err) => console.error('Failed to load apartment blocks:', err),
    );

    const shown = (b: ApartmentBlock) => visible && (filter === null || filter === b.apartmentIndex);

    const pick = (event: PointerEvent): ApartmentBlock | null => {
        const rect = domElement.getBoundingClientRect();
        pointer.set(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1,
        );
        raycaster.setFromCamera(pointer, camera);
        const targets = blocks.filter(shown).map((b) => b.mesh);
        const hit = raycaster.intersectObjects(targets, false)[0];
        return hit ? blocks.find((b) => b.mesh === hit.object) ?? null : null;
    };

    const onPointerMove = (e: PointerEvent) => {
        if (!ready) return;
        hovered = pick(e);
        domElement.style.cursor = hovered ? 'pointer' : '';
    };
    // Click, not pointerdown: pointerdown fires at the start of an orbit drag,
    // so opening a residence there means every camera move that begins on a
    // block throws the visitor into a takeover they did not ask for.
    const onClick = (e: MouseEvent) => {
        if (!ready) return;
        const target = pick(e as unknown as PointerEvent);
        if (target) onSelect(target.apartmentIndex);
    };
    domElement.addEventListener('pointermove', onPointerMove);
    domElement.addEventListener('click', onClick);

    const anchor = new THREE.Vector3();
    const box = new THREE.Box3();

    return {
        get ready() {
            return ready;
        },
        setVisible(next) {
            visible = next;
            group.visible = next;
        },
        setFilter(next) {
            filter = next;
        },
        update(dt, cam) {
            if (!ready) return;
            time += dt;
            for (const block of blocks) {
                const on = shown(block);
                block.mesh.visible = on;

                const uniforms = uniformsOf.get(block.mesh);
                if (uniforms) uniforms.uTime.value = time;
                // Ease the hover so the lift is felt rather than snapped.
                const target = hovered === block ? 1 : 0;
                block.hover += (target - block.hover) * Math.min(dt * 9, 1);
                if (uniforms) uniforms.uHover.value = block.hover;

                if (!on) {
                    block.element.style.display = 'none';
                    continue;
                }

                // Pin the label to the top face of the volume.
                box.setFromObject(block.mesh);
                anchor.set((box.min.x + box.max.x) / 2, box.max.y, (box.min.z + box.max.z) / 2);
                const projected = anchor.clone().project(cam);
                if (projected.z > 1) {
                    block.element.style.display = 'none';
                    continue;
                }
                block.element.style.display = 'flex';
                block.element.style.left = `${(projected.x * 0.5 + 0.5) * window.innerWidth}px`;
                block.element.style.top = `${(-projected.y * 0.5 + 0.5) * window.innerHeight}px`;
                block.element.classList.toggle('is-hover', block.hover > 0.5);
            }
        },
        dispose() {
            domElement.removeEventListener('pointermove', onPointerMove);
            domElement.removeEventListener('click', onClick);
            blocks.forEach((b) => {
                b.element.remove();
                (b.mesh.material as THREE.Material).dispose();
            });
            parent.remove(group);
            draco.dispose();
        },
    };
}

function buildLabel(name: string, caption: string, color: THREE.Color): HTMLElement {
    const el = document.createElement('div');
    el.className = 'ag-block-label';
    el.style.setProperty('--block-accent', `#${color.getHexString()}`);

    const dot = document.createElement('span');
    dot.className = 'ag-block-dot is-round';

    const text = document.createElement('span');
    text.className = 'ag-block-text';
    const title = document.createElement('span');
    title.className = 'ag-block-name';
    title.textContent = name;
    const sub = document.createElement('span');
    sub.className = 'ag-block-caption';
    sub.textContent = caption;
    text.appendChild(title);
    text.appendChild(sub);

    el.appendChild(dot);
    el.appendChild(text);
    return el;
}
